import { Component } from './component.js';
import { ContactSectionComponent } from './finishing-contact-section.js';
import { SizeSelectorComponent } from './finishing-size-selector.js';
import { PricingCalculator } from './finishing-pricing-calculator.js';


/**
 * Finishing Service Form Component
 * Main orchestrator for the finishing service form
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {ContactSectionComponent} contactSection
 * @property {SizeSelectorComponent} sizeSelector
 * @property {HTMLElement} basePriceDisplay
 * @property {HTMLElement} addonsPriceDisplay
 * @property {HTMLElement} monogramPriceDisplay
 * @property {HTMLElement} totalPriceDisplay
 * @property {HTMLInputElement} addMonogramCheckbox
 * @property {HTMLElement} monogramOptions
 * @property {HTMLInputElement} monogramText
 * @property {HTMLSelectElement} monogramPlacement
 * @property {HTMLSelectElement} monogramThread
 * @property {HTMLInputElement} photoInput
 * @property {HTMLElement} photoPreviewContainer
 * @property {HTMLButtonElement} submitButton
 * @extends {Component<Refs>}
 */
export class FinishingServiceFormComponent extends Component {
    static PRODUCT_ID = 'gid://shopify/Product/8964821156082';

    requiredRefs = [
        'form',
        'contactSection',
        'sizeSelector',
        'basePriceDisplay',
        'addonsPriceDisplay',
        'monogramPriceDisplay',
        'totalPriceDisplay',
        'submitButton',
        'discountCodeInput',
        'applyDiscountBtn',
        'discountValidation',
        'discountRow',
        'discountAmountDisplay',
        'appliedDiscount',
        'appliedCode',
        'removeDiscountBtn',
        'descriptionCanvas',
        'specialNotes'
    ];

    /** @type {PricingCalculator | null} */
    #calculator = null;

    /** @type {string} */
    #currentProjectType = '';

    /** @type {File[]} */
    #selectedPhotos = [];

    /** @type {{code: string, type: string, value: number} | null} */
    #appliedDiscount = null;

    /** @type {boolean} */
    #isValidatingDiscount = false;

    connectedCallback() {

        super.connectedCallback();
        this.#loadPricingConfig();

        // Set up manual event listeners after refs are ready
        setTimeout(() => {
            this.#setupEventListeners();
            this.#handleBackNavigation();
        }, 100);
    }

    /**
     * Handle clearing form if user navigated back after success
     */
    #handleBackNavigation() {
        window.addEventListener('pageshow', (event) => {
            // Check if we just submitted successfully
            if (sessionStorage.getItem('finishing_form_submitted') === 'true') {
                this.#resetForm();
                sessionStorage.removeItem('finishing_form_submitted');
            }
        });

        // Also check immediately on load/connection
        if (sessionStorage.getItem('finishing_form_submitted') === 'true') {
            this.#resetForm();
            sessionStorage.removeItem('finishing_form_submitted');
        }
    }

    /**
     * Setup manual event listeners (fallback for on: syntax)
     */
    #setupEventListeners() {

        // Project type radios
        const projectTypeRadios = this.querySelectorAll('input[name="project_type"]');

        projectTypeRadios.forEach(radio => {
            radio.addEventListener('change', (event) => {
                this.onProjectTypeChange(event);
            });
        });

        // Monogram checkbox
        const monogramCheckbox = this.querySelector('#add-monogram');
        if (monogramCheckbox) {
            monogramCheckbox.addEventListener('change', (event) => {
                this.onMonogramToggle(event);
            });
        } else {
            console.warn('⚠️ Monogram checkbox not found');
        }

        // Photo input
        const photoInput = this.querySelector('input[type="file"]');
        if (photoInput) {
            photoInput.addEventListener('change', (event) => {
                this.onPhotoSelect(event);
            });
        }

        // Form submit - CRITICAL: Prevent default form submission
        const form = this.refs.form;
        if (form) {
            form.addEventListener('submit', (event) => {
                this.onFormSubmit(event);
            });
        } else {
            console.warn('⚠️ Form ref not found');
        }

        // Monogram fields - update pricing when info changes
        const monogramFields = this.querySelectorAll('#monogram-text, #monogram-placement, #monogram-size, #monogram-thread');
        monogramFields.forEach(field => {
            field.addEventListener('change', () => this.#updatePricing());
            field.addEventListener('input', () => this.#updatePricing());
        });

        // Discount code input
        if (this.refs.discountCodeInput) {
            this.refs.discountCodeInput.addEventListener('input', () => {
                this.onDiscountCodeInput();
            });
            this.refs.discountCodeInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission, user must click Apply button
                }
            });
        }

        // Apply discount button
        if (this.refs.applyDiscountBtn) {
            this.refs.applyDiscountBtn.addEventListener('click', () => {
                this.applyDiscountCode();
            });
        }

        // Remove discount button
        if (this.refs.removeDiscountBtn) {
            this.refs.removeDiscountBtn.addEventListener('click', () => {
                this.#removeDiscount();
            });
        }

        // Initial sync of monogram fields state
        this.#syncMonogramState();
    }


    /**
     * Load pricing configuration from data attribute (set by Liquid)
     */
    async #loadPricingConfig() {
        try {
            // Get pricing from data attribute (populated by Liquid from metaobject)
            const pricingJson = this.dataset.pricing;

            if (!pricingJson) {
                throw new Error('Pricing data not found');
            }

            const config = JSON.parse(pricingJson);

            this.#calculator = new PricingCalculator(config);
            this.refs.sizeSelector.setPricingConfig(config);
            
            // Pass pillow images if available
            const pillowImagesAttr = this.getAttribute('data-pillow-images');
            if (pillowImagesAttr) {
                try {
                    const pillowImages = JSON.parse(pillowImagesAttr);
                    this.refs.sizeSelector.setPillowImages(pillowImages);
                } catch (e) {
                    console.error('[Form] Failed to parse pillow images:', e);
                }
            }

        } catch (error) {
            console.error('Error loading pricing config:', error);
            alert('Failed to load pricing configuration. Please refresh the page.');
        }
    }

    /**
     * Load discount codes from API for NP-FINISHING product
     */
    /**
     * Handle discount code input changes
     */
    onDiscountCodeInput() {
        const input = this.refs.discountCodeInput;
        const applyBtn = this.refs.applyDiscountBtn;
        const validation = this.refs.discountValidation;
        
        if (!input || !applyBtn) return;
        
        // Skip if input is disabled (discount already applied)
        if (input.disabled) return;
        
        const code = input.value.trim();
        
        // Enable/disable apply button based on input
        applyBtn.disabled = code.length === 0;
        
        // Clear validation message when typing
        if (validation) {
            validation.textContent = '';
            validation.className = 'discount-validation';
        }
        
        // Clear any applied discount if input changes and doesn't match
        if (this.#appliedDiscount) {
            const currentCode = code.toUpperCase();
            const appliedCode = this.#appliedDiscount.code.toUpperCase();
            
            // Only remove if the codes are different (user is typing something else)
            if (currentCode !== appliedCode && !appliedCode.startsWith(currentCode)) {
                this.#removeDiscount();
            }
        }
    }

    /**
     * Apply discount code
     */
    async applyDiscountCode() {
        const input = this.refs.discountCodeInput;
        if (!input) return;
        
        const code = input.value.trim().toUpperCase();
        if (!code) return;
        
        // If already applied, don't revalidate
        if (this.#appliedDiscount && this.#appliedDiscount.code === code) {
            return;
        }
        
        const discountData = await this.#validateDiscountCodeWithServer(code);
        if (discountData) {
            this.#applyDiscount(discountData.code, discountData.type, discountData.value);
        }
    }

    /**
     * Validate discount code with server
     * @param {string} code
     * @returns {Promise<object|null>}
     */
    async #validateDiscountCodeWithServer(code) {
        if (this.#isValidatingDiscount) return null;
        
        const validation = this.refs.discountValidation;
        const applyBtn = this.refs.applyDiscountBtn;
        
        try {
            this.#isValidatingDiscount = true;
            
            if (validation) {
                validation.textContent = 'Validating...';
                validation.className = 'discount-validation validating';
            }
            
            if (applyBtn) {
                applyBtn.disabled = true;
            }
            
            const API_URL = 'https://360.danzia.com/api/validate-discount';

            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_id: FinishingServiceFormComponent.PRODUCT_ID,
                    code: code
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.valid) {
                if (validation) {
                    validation.textContent = '';
                    validation.className = 'discount-validation';
                }
                
                if (applyBtn) {
                    applyBtn.disabled = false;
                }
                
                return data.discount;
            } else {
                if (validation) {
                    validation.textContent = data.message || 'Invalid code';
                    validation.className = 'discount-validation invalid';
                }
                
                if (applyBtn) {
                    applyBtn.disabled = false;
                }
                
                return null;
            }
            
        } catch (error) {
            console.error('❌ Error validating discount code:', error);
            
            if (validation) {
                validation.textContent = 'Unable to validate code';
                validation.className = 'discount-validation error';
            }
            
            if (applyBtn) {
                applyBtn.disabled = false;
            }
            
            return null;
        } finally {
            this.#isValidatingDiscount = false;
        }
    }

    /**
     * Handle project type change
     * @param {Event} event
     */
    onProjectTypeChange(event) {
        const target = /** @type {HTMLInputElement} */ (event.target);
        this.#currentProjectType = target.value;


        this.refs.sizeSelector.updateForProjectType(this.#currentProjectType);
        this.#updatePricing();

    }


    /**
     * Handle size/style selection change
     */
    onSizeChange() {
        this.#updatePricing();
    }

    /**
     * Handle addon checkbox change
     */
    onAddonChange() {
        this.#updatePricing();
    }

    /**
     * Handle monogram checkbox toggle
     * @param {Event | {target: HTMLInputElement}} event
     */
    onMonogramToggle(event) {
        const target = /** @type {HTMLInputElement} */ (event.target);
        const sizeSelector = this.refs.sizeSelector;

        if (!sizeSelector) {
            console.error('[FormComponent] sizeSelector ref not found!');
            return;
        }

        // Access monogram elements through sizeSelector.refs
        const { monogramOptions, monogramText, monogramPlacement, monogramSize, monogramThread } = sizeSelector.refs;


        if (monogramOptions) {
            // Toggle hidden class
            if (target.checked) {
                monogramOptions.classList.remove('hidden');
            } else {
                monogramOptions.classList.add('hidden');
            }

            // Update required state on fields
            if (monogramText) monogramText.required = target.checked;
            if (monogramPlacement) monogramPlacement.required = target.checked;
            if (monogramSize) monogramSize.required = target.checked;
            if (monogramThread) monogramThread.required = target.checked;

            // Toggle visual required marks
            const marks = monogramOptions.querySelectorAll('.monogram-required-mark');
            marks.forEach(mark => {
                if (target.checked) {
                    mark.classList.remove('hidden');
                } else {
                    mark.classList.add('hidden');
                }
            });
        } else {
            console.error('[FormComponent] monogramOptions element not found in sizeSelector!');
        }

        this.#updatePricing();
    }

    /**
     * Initial sync of monogram fields state
     */
    #syncMonogramState() {
        const checkbox = /** @type {HTMLInputElement} */ (this.querySelector('#add-monogram'));
        if (checkbox) {
            this.onMonogramToggle({ target: checkbox });
        }
    }

    /**
     * Handle photo file selection
     * @param {Event} event
     */
    onPhotoSelect(event) {
        const input = /** @type {HTMLInputElement} */ (event.target);
        const files = Array.from(input.files || []);

        // Validate and add files
        const validFiles = [];
        const errors = [];
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        const MAX_FILES = 5;

        // Check total file count
        if (this.#selectedPhotos.length + files.length > MAX_FILES) {
            errors.push(`Maximum ${MAX_FILES} photos allowed`);
        } else {
            for (const file of files) {
                // Validate file type
                if (!file.type.startsWith('image/')) {
                    errors.push(`${file.name}: Not an image file`);
                    continue;
                }

                // Validate file size
                if (file.size > MAX_FILE_SIZE) {
                    errors.push(`${file.name}: File too large (max 10MB)`);
                    continue;
                }

                validFiles.push(file);
            }
        }

        // Display errors
        this.#showPhotoErrors(errors);

        // Add valid files
        if (validFiles.length > 0) {
            this.#selectedPhotos.push(...validFiles);
            this.#renderPhotoPreviews();
        }

        // Reset input
        input.value = '';
    }

    /**
     * Show photo validation errors
     * @param {string[]} errors
     */
    #showPhotoErrors(errors) {
        const container = this.querySelector('.photo-preview-container');
        if (!container) return;

        // Remove existing error messages
        const existingErrors = container.querySelectorAll('.photo-error');
        existingErrors.forEach(el => el.remove());

        // Add new errors
        if (errors.length > 0) {
            const errorDiv = document.createElement('div');
            errorDiv.className = 'photo-error';
            errorDiv.textContent = errors.join(', ');
            container.appendChild(errorDiv);
        }
    }

    /**
     * Render photo preview thumbnails
     */
    #renderPhotoPreviews() {
        const container = this.querySelector('.photo-preview-container');
        if (!container) return;

        // Clear existing previews (except errors)
        const previews = container.querySelectorAll('.photo-preview-item');
        previews.forEach(el => el.remove());

        // Render each photo
        this.#selectedPhotos.forEach((file, index) => {
            const previewItem = document.createElement('div');
            previewItem.className = 'photo-preview-item';

            const img = document.createElement('img');
            img.alt = file.name;

            // Create object URL for preview
            const objectURL = URL.createObjectURL(file);
            img.src = objectURL;
            img.onload = () => URL.revokeObjectURL(objectURL);

            const removeBtn = document.createElement('button');
            removeBtn.className = 'photo-preview-remove';
            removeBtn.type = 'button';
            removeBtn.innerHTML = '×';
            removeBtn.onclick = () => this.#removePhoto(index);

            previewItem.appendChild(img);
            previewItem.appendChild(removeBtn);
            container.appendChild(previewItem);
        });
    }

    /**
     * Remove a photo from selection
     * @param {number} index
     */
    #removePhoto(index) {
        this.#selectedPhotos.splice(index, 1);
        this.#renderPhotoPreviews();
    }

    /**
     * Convert photos to base64
     * @returns {Promise<Array<{filename: string, data: string}>>}
     */
    async #convertPhotosToBase64() {
        const promises = this.#selectedPhotos.map(file => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve({
                    filename: file.name,
                    data: /** @type {string} */ (reader.result)
                });
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        });

        return Promise.all(promises);
    }

    /**
     * Handle monogram input change
     */
    onMonogramChange() {
        this.#updatePricing();
    }

    /**
     * Apply selected discount
     */
    #applyDiscount(code, type, value) {
        this.#appliedDiscount = { code, type, value };
        
        // Update UI
        if (this.refs.appliedCode) {
            this.refs.appliedCode.textContent = code;
        }
        
        if (this.refs.appliedDiscount) {
            this.refs.appliedDiscount.classList.remove('hidden');
        }
        
        // Disable input first, then set value to prevent triggering input event handler
        if (this.refs.discountCodeInput) {
            this.refs.discountCodeInput.disabled = true;
            this.refs.discountCodeInput.value = code;
        }
        
        if (this.refs.applyDiscountBtn) {
            this.refs.applyDiscountBtn.disabled = true;
        }
        
        if (this.refs.discountValidation) {
            this.refs.discountValidation.textContent = '';
            this.refs.discountValidation.className = 'discount-validation';
        }
        
        // Recalculate pricing
        this.#updatePricing();
    }

    /**
     * Remove applied discount
     */
    #removeDiscount() {
        this.#appliedDiscount = null;
        
        // Update UI
        if (this.refs.appliedDiscount) {
            this.refs.appliedDiscount.classList.add('hidden');
        }
        
        // Re-enable input
        if (this.refs.discountCodeInput) {
            this.refs.discountCodeInput.value = '';
            this.refs.discountCodeInput.disabled = false;
        }
        
        if (this.refs.applyDiscountBtn) {
            this.refs.applyDiscountBtn.disabled = true;
        }
        
        if (this.refs.discountValidation) {
            this.refs.discountValidation.textContent = '';
            this.refs.discountValidation.className = 'discount-validation';
        }
        
        // Recalculate pricing
        this.#updatePricing();
    }

    /**
     * Update pricing display
     */
    #updatePricing() {
        if (!this.#calculator) return;

        const selection = this.refs.sizeSelector.getSelection();
        const monogramData = this.#getMonogramData();

        const pricing = this.#calculator.calculate(selection, monogramData);
        let subtotal = pricing.total;

        // Calculate discount
        let discountAmount = 0;
        if (this.#appliedDiscount) {
            if (this.#appliedDiscount.type === 'percentage') {
                // value is decimal (e.g. 0.4 for 40%), no need to divide by 100
                discountAmount = subtotal * this.#appliedDiscount.value;
            } else if (this.#appliedDiscount.type === 'fixed') {
                discountAmount = this.#appliedDiscount.value;
            }
        }

        const finalTotal = Math.max(0, subtotal - discountAmount);

        // Update displays
        this.refs.basePriceDisplay.textContent = PricingCalculator.formatPrice(pricing.base);
        this.refs.addonsPriceDisplay.textContent = PricingCalculator.formatPrice(pricing.addons);
        this.refs.monogramPriceDisplay.textContent = PricingCalculator.formatPrice(pricing.monogram);

        // Show/hide discount row
        if (this.refs.discountRow) {
            if (discountAmount > 0) {
                this.refs.discountRow.classList.remove('hidden');
                if (this.refs.discountAmountDisplay) {
                    this.refs.discountAmountDisplay.textContent = '-' + PricingCalculator.formatPrice(discountAmount);
                }
            } else {
                this.refs.discountRow.classList.add('hidden');
            }
        }

        this.refs.totalPriceDisplay.textContent = PricingCalculator.formatPrice(finalTotal);
    }

    /**
     * Get monogram configuration
     * @returns {object}
     */
    #getMonogramData() {
        const sizeSelector = this.refs.sizeSelector;

        if (!sizeSelector) {
            return { enabled: false };
        }

        const addMonogramCheckbox = this.querySelector('#add-monogram');
        const { monogramText, monogramPlacement, monogramSize, monogramThread } = sizeSelector.refs;

        if (!addMonogramCheckbox?.checked) {
            return { enabled: false };
        }

        return {
            enabled: true,
            text: monogramText?.value || '',
            placement: monogramPlacement?.value || '',
            size: monogramSize?.value || 'medium', // default to medium if not selected
            threadType: monogramThread?.value || 'regular',
            metallic: monogramThread?.value === 'metallic'
        };
    }

    /**
     * Handle form submission
     * @param {Event} event
     */
    async onFormSubmit(event) {
        event.preventDefault();

        // Validate contact section
        const contactValidation = this.refs.contactSection.validate();
        if (!contactValidation.isValid) {
            alert('Please correct the following errors:\n' + contactValidation.errors.join('\n'));
            return;
        }

        // Gather all form data
        const formData = this.#gatherFormData();

        // Convert photos to base64
        if (this.#selectedPhotos.length > 0) {
            formData.photos = await this.#convertPhotosToBase64();
        }
        // Update submit button state
        this.#setSubmitting(true);


        try {
            // Call serverless API
            const response = await this.#submitToAPI(formData);

            // Redirect to payment URL if available
            if (response.payment_url) {
                // Set flag so if they click back, the form clears
                sessionStorage.setItem('finishing_form_submitted', 'true');
                window.location.href = response.payment_url;
            } else {
                alert('Request submitted successfully! You will receive payment instructions via email.');
                this.#resetForm();
            }

        } catch (error) {
            console.error('❌ Submission error:', error);
            alert(`There was an error submitting your request: ${error.message}\n\nPlease try again or contact support.`);
        } finally {
            this.#setSubmitting(false);
        }
    }

    /**
     * Submit form data to serverless API
     * @param {object} formData
     * @returns {Promise<object>}
     */
    async #submitToAPI(formData) {
        const API_URL = 'https://360.danzia.com/api/';


        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                // Try to get error details
                const contentType = response.headers.get('content-type');
                let errorData = {};
                let errorText = '';

                try {
                    errorText = await response.text();

                    // Try to parse as JSON
                    if (contentType && contentType.includes('application/json')) {
                        errorData = JSON.parse(errorText);
                    }
                } catch (parseError) {
                    console.error('Failed to parse error response:', parseError);
                }

                const errorMessage = errorData.error || errorText || `API request failed with status ${response.status}`;
                console.error('❌ API Error:', errorMessage);
                throw new Error(errorMessage);
            }

            const result = await response.json();
            return result;

        } catch (error) {
            console.error('❌ Fetch error:', error);
            console.error('❌ Error stack:', error.stack);
            throw error;
        }
    }

    /**
     * Gather all form data
     * @returns {object}
     */
    #gatherFormData() {
        const contact = this.refs.contactSection.getData();
        const selection = this.refs.sizeSelector.getSelection();
        const monogram = this.#getMonogramData();

        const pricing = this.#calculator.calculate(selection, monogram);

        const notesTextarea = this.refs.form.querySelector('textarea[name="notes"]');

        // Calculate final pricing with discount
        let subtotal = pricing.total;
        let discountAmount = 0;

        if (this.#appliedDiscount) {
            if (this.#appliedDiscount.type === 'percentage') {
                discountAmount = subtotal * (this.#appliedDiscount.value / 100);
            } else if (this.#appliedDiscount.type === 'fixed') {
                discountAmount = this.#appliedDiscount.value;
            }
        }

        const finalTotal = Math.max(0, subtotal - discountAmount);

        return {
            contact,
            project_type: selection.type,  // Top-level field for metaobject
            product_id: FinishingServiceFormComponent.PRODUCT_ID, // Use class constant
            request: {
                ...selection,
                monogram: monogram.enabled ? monogram : null,
                special_notes: (/** @type {HTMLTextAreaElement} */ (this.refs.specialNotes))?.value || ''
            },
            pricing: {
                ...pricing,
                discount: discountAmount,
                discount_code: this.#appliedDiscount?.code,
                final_total: finalTotal
            },
            estimated_price: finalTotal,
            description_canvas: (/** @type {HTMLInputElement} */ (this.refs.descriptionCanvas))?.value || '',
            submittedAt: new Date().toISOString()
        };
    }

    /**
     * Set form submitting state
     * @param {boolean} isSubmitting
     */
    #setSubmitting(isSubmitting) {
        const { submitButton } = this.refs;

        submitButton.disabled = isSubmitting;
        submitButton.textContent = isSubmitting ? 'Submitting...' : 'Submit';
    }

    /**
     * Reset form state
     */
    #resetForm() {
        // Reset HTML form
        this.refs.form.reset();

        // Reset photos
        this.#selectedPhotos = [];
        this.#renderPhotoPreviews();

        // Remove discount
        if (this.#appliedDiscount) {
            this.#removeDiscount();
        }

        // Reset Monogram UI (hide fields)
        const monogramCheckbox = this.querySelector('#add-monogram');
        if (monogramCheckbox) {
            // Dispatch change event to trigger listeners that hide fields
            monogramCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Sync Project Type state
        const checkedRadio = this.querySelector('input[name="project_type"]:checked');
        if (checkedRadio) {
            // Dispatch change event to sync state
            checkedRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Final pricing update
        this.#updatePricing();
    }
}

if (!customElements.get('finishing-service-form-component')) {
    customElements.define('finishing-service-form-component', FinishingServiceFormComponent);
}
